import * as THREE from "three";
import { MapView, MapBoxProvider } from "geo-three";
import { Constants } from "../constants";
import { OrthophotoMapProvider } from "../geo-three/orthophoto-map-provider";
import { LODCameraDistance } from "../geo-three/lod-camera-distance";
import { CustomMapPlaneNode } from "../geo-three/custom-map-plane-node";
import { CustomMapView } from "../geo-three/custom-map-view";
import { Utils } from "../utils/utils";
import { ViewType } from "../data-structures/view-type";

/**
 * Manager responsible for managing the base map and orthophoto overlays,
 * including loading, visibility, and bounding box calculations.
 */
export class OrthophotoManager {
	private readonly sateliteProvider = new MapBoxProvider(Constants.mapBoxAPIKey, "mapbox.satellite", MapBoxProvider.MAP_ID, "jpg70", false);
	private readonly vectorMapProvider = new MapBoxProvider(Constants.mapBoxAPIKey, "mapbox/dark-v10", MapBoxProvider.STYLE);
	private readonly lod = new LODCameraDistance();
	private readonly mapGroup: THREE.Group;
	private readonly orthophotoGroup: THREE.Group;
	private readonly map: MapView;
	private orthophotoBounds: number[] = [];
	public orthophotoMaps: MapView[] = [];
	public mapType: "satellite" | "vector" = "vector";

	public constructor(private readonly viewer: any) {
		this.mapGroup = new THREE.Group();
		this.mapGroup.name = "MapGroup";
		this.viewer.scene.scene.add(this.mapGroup);

		this.orthophotoGroup = new THREE.Group();
		this.orthophotoGroup.name = "OrthophotoGroup";
		this.viewer.scene.scene.add(this.orthophotoGroup);

		//Setup map
		MapView.mapModes.set(300, CustomMapPlaneNode);

		const map: MapView = new CustomMapView(300, this.mapType == "satellite" ? this.sateliteProvider : this.vectorMapProvider);
		map.lod = this.lod;
		map.name = "Map";
		//Rotate the map so that x and y axis will be on the ground plane
		map.rotation.set(Math.PI * -0.5, Math.PI, Math.PI);
		map.visible = this.viewer.viewType == ViewType.View2D;

		this.map = map;
	}

	public setMapType(mapType: "satellite" | "vector"): void {
		this.mapType = mapType;
		this.map.setProvider(this.mapType == "vector" ? this.vectorMapProvider : this.sateliteProvider);
	}

	public loadOrthophoto(baseUrl: string, name: string, bounds: number[], minZoom: number, maxZoom: number, uuid: string): void {
		//Othophoto map
		const orthophotoProvider = new OrthophotoMapProvider(baseUrl) as any;
		orthophotoProvider.bounds = bounds;
		this.orthophotoBounds = bounds;
		orthophotoProvider.minZoom = minZoom || 16;
		orthophotoProvider.maxZoom = maxZoom || 20;
		const orthophotoMap: MapView = new CustomMapView(300, orthophotoProvider);
		orthophotoMap.name = baseUrl;
		orthophotoMap.uuid = uuid;
		orthophotoMap.lod = this.lod;
		//Rotate the map so that x and y axis will be on the ground plane
		orthophotoMap.rotation.set(Math.PI * 0.5, 0, 0);
		orthophotoMap.name = name;
		orthophotoMap.position.set(0, 0, 10);

		this.orthophotoMaps.push(orthophotoMap);

		if (this.viewer.viewType == ViewType.View2D) {
			this.orthophotoGroup.add(orthophotoMap);
		}

		this.viewer.updateLayers();
	}

	public syncViewType(viewType: ViewType): void {
		if (viewType == ViewType.View3D) {
			//Remove map and orthophotos
			this.mapGroup.remove(this.map);
			for (const orthophotoMap of this.orthophotoMaps) {
				this.orthophotoGroup.remove(orthophotoMap);
			}
		} else {
			//If input coordinate system is known then show the map otherwise don't because
			//it is probably not known where to show the orthophoto on map exactly
			if (this.viewer.projectCoordinateSystem) {
				this.mapGroup.add(this.map);
			}

			//Show orthophotos
			for (const orthophotoMap of this.orthophotoMaps) {
				this.orthophotoGroup.add(orthophotoMap);
			}
		}

		this.map.visible = viewType == ViewType.View2D;
		for (const op of this.orthophotoMaps) {
			op.visible = viewType == ViewType.View2D;
		}
	}

	public update(camera: any): void {
		if (this.map) {
			this.map.update(camera, this.viewer.renderer, this.viewer.scene.scene);
		}
		for (const map of this.orthophotoMaps) {
			map.update(camera, this.viewer.renderer, this.viewer.scene.scene);
		}
	}

	public getOrthophotoBoundsBox(): THREE.Box3 | null {
		if (this.orthophotoBounds && this.orthophotoBounds.length == 4) {
			return this.boundsToBox(this.orthophotoBounds);
		}
		return null;
	}

	public getVisibleOrthophotoBox(): THREE.Box3 | null {
		const visibleOrtho = this.orthophotoMaps.find((op) => op.visible && (op as any).provider?.bounds?.length === 4);
		if (visibleOrtho) {
			return this.boundsToBox((visibleOrtho as any).provider.bounds);
		}
		return null;
	}

	public getBoxForId(id: string): THREE.Box3 | null {
		const map = this.orthophotoMaps.find((m) => m.uuid == id);
		const bounds = (map as any)?.provider?.bounds;
		if (map && bounds) {
			return this.boundsToBox(bounds);
		}
		return null;
	}

	public cleanup(): void {
		while (this.orthophotoMaps.length > 0) {
			const e = this.orthophotoMaps[0];
			this.orthophotoMaps.splice(0, 1);
			if (e.parent) {
				e.parent.remove(e);
			}
		}
	}

	private boundsToBox(bounds: number[]): THREE.Box3 {
		const coord1 = Utils.latLongToXYZ(bounds[0], bounds[1]);
		const coord2 = Utils.latLongToXYZ(bounds[2], bounds[3]);
		const min = new THREE.Vector3(coord1.x, coord1.y, 0);
		const max = new THREE.Vector3(coord2.x, coord2.y, 0);
		return new THREE.Box3(min, max);
	}
}
